import { SidemenuContext } from "@/contexts/Sidemenu";
import { useUserStore } from "@/store/useUserStore";
import {
	AppBar,
	Toolbar,
	IconButton,
	Box,
	Button,
	Avatar,
	Typography,
	type SvgIconProps,
	SvgIcon,
} from "@mui/material";
import { useRouter } from "next/router";
import { AUTH_ROUTES, ROUTES } from "../constants/Routes";
import { useContext } from "react";
import { blueGrey } from "@mui/material/colors";
import theme from "@/styles/theme";
import Link from "next/link";
import { k2d } from "@/fonts/k2d";
import { MenuOpenRounded as MenuCloseIcon } from "@mui/icons-material";
import { QuickGuides } from "@/components/QuickGuides";
import { CustomTooltip } from "@/components/CustomTooltip";
import { useAbility } from "@/hooks/useAbility";

const MenuOpenIcon = (props: SvgIconProps) => (
	<SvgIcon {...props} viewBox='0 0 24 24'>
		<path d='M13.0003 7C13.0003 7.55 12.5503 8 12.0003 8H4.00027C3.45027 8 3.00027 7.55 3.00027 7C3.00027 6.45 3.45027 6 4.00027 6H12.0003C12.5503 6 13.0003 6.45 13.0003 7ZM15.3 14.88L18.18 12L15.3 9.12C14.91 8.73 14.91 8.1 15.3 7.71C15.3925 7.6173 15.509 7.54375 15.63 7.49357C15.751 7.44339 15.879 7.41756 16.01 7.41756C16.141 7.41756 16.269 7.44339 16.39 7.49357C16.511 7.54375 16.6175 7.6173 16.71 7.71L20.3003 11.3C20.6903 11.69 20.6903 12.32 20.3003 12.71L16.71 16.3C16.32 16.69 15.69 16.69 15.3 16.3C14.92 15.91 14.91 15.27 15.3 14.88Z' />
		<path d='M13.5 13C14.05 13 14.5 12.55 14.5 12C14.5 11.45 14.05 11 13.5 11H4.00027C3.45027 11 3.00027 11.45 3.00027 12C3.00027 12.55 3.45027 13 4.00027 13H13.5Z' />
		<path d='M12.0003 18C12.5503 18 13.0003 17.55 13.0003 17C13.0003 16.45 12.5503 16 12.0003 16H4.00027C3.45027 16 3.00027 16.45 3.00027 17C3.00027 17.55 3.45027 18 4.00027 18H12.0003Z' />
	</SvgIcon>
);

function UserAvatar({ initials }: { initials: string }) {
	return (
		<CustomTooltip
			placement='bottom-end'
			offsetX={0}
			offsetY={-8}
			title={
				<Typography variant='body2' fontSize='0.8rem'>
					Acessar perfil
				</Typography>
			}
		>
			<Avatar
				component={Link}
				href={ROUTES.AUTH.PROFILE}
				sx={{
					width: 36,
					height: 36,
					fontSize: 15,
					background: `linear-gradient(to top left, ${theme.palette.success.light}, ${theme.palette.success.main})`,
					cursor: "pointer",
					textDecorationLine: "none",
				}}
			>
				{initials}
			</Avatar>
		</CustomTooltip>
	);
}

function LoginButton() {
	return (
		<Button
			component={Link}
			href={ROUTES.AUTH.LOGIN}
			variant='outlined'
			sx={{
				textDecorationLine: "none",
				color: blueGrey["50"],
				borderColor: blueGrey["50"],
				textTransform: "uppercase",
				fontSize: "13px",
				padding: "4px 12px",
				"&:hover": {
					borderColor: "transparent",
					backgroundColor: "rgba(0, 0, 0, 0.2)",
				},
			}}
		>
			Login
		</Button>
	);
}

export function Header() {
	const ability = useAbility();
	const canAccessAccount = ability.can("update", "Account");
	const { pathname } = useRouter();
	const initials = useUserStore((s) => s.initials);
	const { isOpen, handler } = useContext(SidemenuContext);

	const isAuthPage = (AUTH_ROUTES as string[]).includes(pathname);

	return (
		<AppBar
			position='static'
			sx={{ background: theme.gradient.main, paddingInline: 3 }}
		>
			<Toolbar
				sx={{
					justifyContent: "space-between",
					minHeight: "auto",
					padding: "0 !important",
				}}
			>
				<Box display='flex' alignItems='center' gap={1}>
					{!isAuthPage && (
						<IconButton
							size='small'
							color='inherit'
							onClick={isOpen ? handler.close : handler.open}
							title={isOpen ? "Recolher menu lateral" : "Expandir menu lateral"}
							sx={{ marginLeft: "-5px" }}
						>
							{isOpen ? <MenuCloseIcon /> : <MenuOpenIcon />}
						</IconButton>
					)}
					<Link
						href='/'
						style={{ textDecoration: "none" }}
						title='Retornar ao início'
					>
						<Typography
							variant='h4'
							fontSize='1.7rem'
							color='#FFF'
							fontFamily={k2d.style.fontFamily}
						>
							RAIP
						</Typography>
					</Link>
				</Box>

				{!isAuthPage && (
					<Box sx={{ display: "flex", gap: 3, alignItems: "center" }}>
						<QuickGuides />
						{canAccessAccount ? (
							<UserAvatar initials={initials ?? ""} />
						) : (
							<LoginButton />
						)}
					</Box>
				)}
			</Toolbar>
		</AppBar>
	);
}
